"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, ArrowLeft, RotateCcw, Activity, Layers, ShieldCheck, Cpu, Sparkles, ClipboardCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const steps = [{
  id: 1,
  title: "Operating Industry",
  description: "Select the operational setting and regulatory framework of your facility.",
  icon: "industry"
}, {
  id: 2,
  title: "Analytical Objective",
  description: "Identify the primary measurement challenge or process endpoint to address.",
  icon: "objective"
}, {
  id: 3,
  title: "Deployment Mode",
  description: "Choose where the instrumentation or measurement interface will sit.",
  icon: "deployment"
}, {
  id: 4,
  title: "Compliance & Safety",
  description: "Specify critical compliance, zoning, and qualification prerequisites.",
  icon: "compliance"
}];
const industryOptions = [{
  value: "pharma",
  label: "Pharmaceutical Manufacturing",
  detail: "Finished dosage forms, OSD & sterile processing"
}, {
  value: "biopharma",
  label: "Biopharmaceutical",
  detail: "Upstream cell culture, downstream purification"
}, {
  value: "chemical",
  label: "Chemical & Ingredients",
  detail: "Continuous synthesis, solvent recovery & bulk solids"
}, {
  value: "defense",
  label: "Security & First Response",
  detail: "Field chemical identification & trace detection"
}];
const objectiveOptions = [{
  value: "pat",
  label: "Process Analytical (PAT)",
  detail: "Blend uniformity, drying endpoints & real-time release"
}, {
  value: "rmid",
  label: "Raw Material ID",
  detail: "Rapid incoming receipt verification through containers"
}, {
  value: "physical",
  label: "Powder & Physical Testing",
  detail: "Flowability, tapped density, volume & hardness"
}, {
  value: "reaction",
  label: "Solvent Recovery & Reaction",
  detail: "HPLC solvent purification & continuous monitoring"
}];
const deploymentOptions = [{
  value: "inline",
  label: "In-line / On-line (Process)",
  detail: "Direct immersion probe or optical flow cell"
}, {
  value: "atline",
  label: "At-line / Near-line",
  detail: "Dedicated production-floor verification station"
}, {
  value: "lab",
  label: "Benchtop QC Laboratory",
  detail: "High-precision automated testing & batch release"
}, {
  value: "handheld",
  label: "Handheld / Field Portable",
  detail: "Battery-powered point-of-need inspection"
}];
const complianceOptions = [{
  value: "gmp",
  label: "Strict cGMP / 21 CFR Part 11",
  detail: "Full IQ/OQ qualification protocols & data integrity"
}, {
  value: "atex",
  label: "ATEX / Hazardous Zone Rated",
  detail: "Intrinsically safe / explosion-proof installation"
}, {
  value: "iso",
  label: "Standard ISO / GLP",
  detail: "Routine laboratory compliance & traceable calibration"
}, {
  value: "academic",
  label: "R&D / Academic Exploratory",
  detail: "Flexible research workflows & method development"
}];
export default function Assessment() {
  const [currentStep, setCurrentStep] = useState(1);
  const [industry, setIndustry] = useState("pharma");
  const [objective, setObjective] = useState("pat");
  const [deployment, setDeployment] = useState("inline");
  const [compliance, setCompliance] = useState("gmp");
  const progressPercentage = currentStep / steps.length * 100;
  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleReset = () => {
    setCurrentStep(1);
    setIndustry("pharma");
    setObjective("pat");
    setDeployment("inline");
    setCompliance("gmp");
  };
  const getAssessmentSummary = () => {
    let technologyRecommendation = "Process NIR / Raman Spectrometry";
    let rationale = "Integrated optical monitoring with chemometric quantitative modelling.";
    let qualificationFocus = "Complete IQ/OQ validation and lifecycle documentation.";
    if (objective === "physical") {
      technologyRecommendation = "Automated Powder Flow & Physical Tablet Testing Suite";
      rationale = "Pharmacopeia-compliant bulk density, tapped density, and flow rate characterisation.";
    } else if (objective === "rmid" && deployment === "handheld") {
      technologyRecommendation = "1064nm Handheld Raman Analyzer Platform";
      rationale = "Fluorescence-free raw material verification directly through amber bottles, bags, and liners.";
    } else if (objective === "reaction") {
      technologyRecommendation = "Continuous Optical Process Guardian & Solvent Recovery System";
      rationale = "Real-time fractional monitoring and automated purity loop control.";
    } else if (objective === "pat" && deployment === "inline") {
      technologyRecommendation = "Miniaturised Solid-State MicroNIR PAT Transflectance/Reflectance Engine";
      rationale = "Flange-mounted continuous blend uniformity and moisture tracking with real-time feedback.";
    }
    if (compliance === "atex") {
      qualificationFocus = "ATEX Zone 0/20 certified optical heads with purged control enclosures.";
    } else if (compliance === "gmp") {
      qualificationFocus = "21 CFR Part 11 compliant audit trails, electronic signatures, and IQ/OQ execution.";
    } else {
      qualificationFocus = "Streamlined factory calibration certificates with routine preventative checks.";
    }
    return {
      technologyRecommendation,
      rationale,
      qualificationFocus
    };
  };
  const summary = getAssessmentSummary();
  const getStepIcon = (iconName: string) => {
    switch (iconName) {
      case "industry":
        return <Layers className="size-4 text-primary" />;
      case "objective":
        return <Activity className="size-4 text-primary" />;
      case "deployment":
        return <Cpu className="size-4 text-primary" />;
      case "compliance":
        return <ShieldCheck className="size-4 text-primary" />;
      default:
        return <ClipboardCheck className="size-4 text-primary" />;
    }
  };
  return <section id="interactiveapplicationassessment" data-nav="light" className="py-16 md:py-24 bg-background text-foreground border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-2xl mx-auto text-center mb-12">
          <Badge variant="secondary" className="mb-3">
            Interactive Assessment Tool
          </Badge>
          <h2 className="text-3xl md:text-4xl font-default font-semibold tracking-tight text-foreground">
            Analytical System Matcher
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            Complete this four-step diagnostic to receive a tailored configuration summary for your laboratory or manufacturing process.
          </p>
        </div>

        {/* Assessment Card */}
        <div className="max-w-3xl mx-auto">
          <Card className="border border-border shadow-md bg-card">
            {/* Step Progress Tracker */}
            <CardHeader className="border-b border-border pb-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {currentStep <= 4 ? `Step 0${currentStep} of 04` : "Assessment Complete"}
                    </span>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs font-medium text-muted-foreground">
                      {currentStep <= 4 ? steps[currentStep - 1].title : "Application Overview"}
                    </span>
                  </div>
                  {currentStep === 5 && <Button variant="ghost" size="sm" onClick={handleReset} className="text-xs text-muted-foreground hover:text-foreground">
                      <RotateCcw className="size-3.5 mr-1" />
                      Restart
                    </Button>}
                </div>
                <Progress value={currentStep === 5 ? 100 : progressPercentage} className="h-1.5" />
              </div>

              {currentStep <= 4 && <div className="mt-4">
                  <div className="flex items-center gap-2">
                    {getStepIcon(steps[currentStep - 1].icon)}
                    <CardTitle className="text-xl font-default text-card-foreground">
                      {steps[currentStep - 1].title}
                    </CardTitle>
                  </div>
                  <CardDescription className="mt-1">
                    {steps[currentStep - 1].description}
                  </CardDescription>
                </div>}
            </CardHeader>

            <CardContent className="pt-6">
              <AnimatePresence mode="wait">
                {/* Step 1: Industry */}
                {currentStep === 1 && <motion.div key="step-1" initial={{
                opacity: 0,
                x: 12
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -12
              }} transition={{
                duration: 0.25
              }} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {industryOptions.map((item, index) => {
                  const isSelected = industry === item.value;
                  return <div key={item.value} data-index={index} onClick={() => setIndustry(item.value)} className={`cursor-pointer rounded-lg border p-4 text-left transition-[border-color,background-color,box-shadow] ${isSelected ? "border-primary bg-primary/5 shadow-xs" : "border-border bg-card hover:border-primary/40 hover:bg-muted/40"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-semibold text-sm text-foreground">
                              {item.label}
                            </span>
                            <div className={`mt-0.5 size-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}>
                              {isSelected && <CheckCircle2 className="size-3" />}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            {item.detail}
                          </p>
                        </div>;
                })}
                  </motion.div>}

                {/* Step 2: Objective */}
                {currentStep === 2 && <motion.div key="step-2" initial={{
                opacity: 0,
                x: 12
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -12
              }} transition={{
                duration: 0.25
              }} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {objectiveOptions.map((item, index) => {
                  const isSelected = objective === item.value;
                  return <div key={item.value} data-index={index} onClick={() => setObjective(item.value)} className={`cursor-pointer rounded-lg border p-4 text-left transition-[border-color,background-color,box-shadow] ${isSelected ? "border-primary bg-primary/5 shadow-xs" : "border-border bg-card hover:border-primary/40 hover:bg-muted/40"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-semibold text-sm text-foreground">
                              {item.label}
                            </span>
                            <div className={`mt-0.5 size-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}>
                              {isSelected && <CheckCircle2 className="size-3" />}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            {item.detail}
                          </p>
                        </div>;
                })}
                  </motion.div>}

                {/* Step 3: Deployment */}
                {currentStep === 3 && <motion.div key="step-3" initial={{
                opacity: 0,
                x: 12
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -12
              }} transition={{
                duration: 0.25
              }} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {deploymentOptions.map((item, index) => {
                  const isSelected = deployment === item.value;
                  return <div key={item.value} data-index={index} onClick={() => setDeployment(item.value)} className={`cursor-pointer rounded-lg border p-4 text-left transition-[border-color,background-color,box-shadow] ${isSelected ? "border-primary bg-primary/5 shadow-xs" : "border-border bg-card hover:border-primary/40 hover:bg-muted/40"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-semibold text-sm text-foreground">
                              {item.label}
                            </span>
                            <div className={`mt-0.5 size-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}>
                              {isSelected && <CheckCircle2 className="size-3" />}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            {item.detail}
                          </p>
                        </div>;
                })}
                  </motion.div>}

                {/* Step 4: Compliance */}
                {currentStep === 4 && <motion.div key="step-4" initial={{
                opacity: 0,
                x: 12
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -12
              }} transition={{
                duration: 0.25
              }} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {complianceOptions.map((item, index) => {
                  const isSelected = compliance === item.value;
                  return <div key={item.value} data-index={index} onClick={() => setCompliance(item.value)} className={`cursor-pointer rounded-lg border p-4 text-left transition-[border-color,background-color,box-shadow] ${isSelected ? "border-primary bg-primary/5 shadow-xs" : "border-border bg-card hover:border-primary/40 hover:bg-muted/40"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-semibold text-sm text-foreground">
                              {item.label}
                            </span>
                            <div className={`mt-0.5 size-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}>
                              {isSelected && <CheckCircle2 className="size-3" />}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            {item.detail}
                          </p>
                        </div>;
                })}
                  </motion.div>}

                {/* Step 5: Final Summary Output */}
                {currentStep === 5 && <motion.div key="step-summary" initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -12
              }} transition={{
                duration: 0.3
              }} className="space-y-6">
                    <div className="rounded-lg bg-secondary/50 border border-border p-5 space-y-4">
                      <div className="flex items-center gap-2">
                        <Sparkles className="size-4 text-primary shrink-0" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                          Suggested Analytical Configuration
                        </span>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-default font-semibold text-foreground">
                          {summary.technologyRecommendation}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {summary.rationale}
                        </p>
                      </div>

                      <div className="pt-3 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-muted-foreground block">Qualification Target:</span>
                          <span className="font-medium text-foreground">{summary.qualificationFocus}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Deployment Alignment:</span>
                          <span className="font-medium text-foreground capitalize">
                            {deployment} • {industry}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-card border border-border rounded-lg p-4">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                        Your Selected Parameters
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs bg-background">
                          Industry: {industryOptions.find(i => i.value === industry)?.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-background">
                          Objective: {objectiveOptions.find(o => o.value === objective)?.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-background">
                          Mode: {deploymentOptions.find(d => d.value === deployment)?.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-background">
                          Standard: {complianceOptions.find(c => c.value === compliance)?.label}
                        </Badge>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Navigation Controls */}
              <div className="mt-8 pt-4 border-t border-border flex items-center justify-between gap-3">
                {currentStep > 1 && currentStep <= 4 ? <Button variant="outline" onClick={handleBack} className="text-xs font-medium">
                    <ArrowLeft className="size-3.5 mr-1" />
                    Previous Step
                  </Button> : <div />}

                {currentStep < 4 && <Button variant="default" onClick={handleNext} className="ml-auto text-xs font-medium">
                    Continue
                    <ArrowRight className="size-3.5 ml-1" />
                  </Button>}

                {currentStep === 4 && <Button variant="default" onClick={handleNext} className="ml-auto text-xs font-medium">
                    Generate Summary
                    <ArrowRight className="size-3.5 ml-1" />
                  </Button>}

                {currentStep === 5 && <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-3">
                    <Button variant="outline" onClick={handleReset} className="w-full sm:w-auto text-xs font-medium">
                      <RotateCcw className="size-3.5 mr-1" />
                      Revise Selections
                    </Button>
                    <Button asChild variant="default" className="w-full sm:w-auto text-xs font-medium">
                      <Link to="/DiscussYourApplicationPage">
                        Discuss Your Application
                        <ArrowRight className="size-3.5 ml-1" />
                      </Link>
                    </Button>
                  </div>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}